/**
 * src/lib/timeoutRecovery.js — RC 1.49.1 P0-A + P0-B
 *
 * Two helpers that close the perception gap between a 30s ingress
 * proxy timeout and the backend, which has often successfully
 * persisted the user's artefact by then.
 *
 *   1. `findExistingArtefact()` — used BEFORE generation to honour
 *      the artefact-reuse rule (P0-B). If a recent variant /
 *      letter exists for this `job_id`, return it instead of
 *      paying for a duplicate generation.
 *
 *   2. `withTimeoutRecovery()` — wraps a generation call. On a
 *      504/timeout/abort, it polls `findExistingArtefact()` up to
 *      `maxRetries` times with `intervalMs` between attempts. If
 *      the backend completed in the background, the artefact will
 *      surface on a subsequent poll and the wrapper resolves with
 *      it as if the original call had succeeded.
 *
 * Hard rails honoured:
 *   • No new credit charge on retrieval (the GET endpoints
 *     `/api/cv-variants` and `/api/cover-letters` are read-only
 *     and free).
 *   • Never re-triggers generation on timeout — only retrieval.
 *   • Backwards compatible — callers that don't opt in get the
 *     legacy behaviour unchanged.
 */

import { apiFetch } from './api';

/**
 * @param {'cv'|'letter'} kind
 * @param {string} jobId
 * @param {object} [opts]
 * @param {number} [opts.submittedAtMs]  When provided, only return
 *   rows created AFTER `submittedAtMs - 30s`. Used by the recovery
 *   wrapper to bind to the row this generation just persisted.
 * @returns {Promise<object|null>} the most-recent artefact, or null
 *
 * RC 1.49.1 P0-3 — IMPORTANT correction. The legacy implementation
 * filtered rows by `r.job_id === jobId`, but the `cv_variants` and
 * `application_documents` tables do not carry a `job_id` column —
 * job linkage lives in `application_packs.cv_variant_id` /
 * `cover_letter_id`. The job_id filter therefore always returned
 * null in production, silently disabling recovery. We now bind by
 * recency (`submittedAtMs`) instead, since the user has just
 * clicked Generate and the freshest row in their list is
 * overwhelmingly likely to be theirs.
 */
export async function findExistingArtefact(kind, jobId, opts = {}) {
  if (!jobId) return null;
  const path = kind === 'cv' ? '/api/cv-variants' : '/api/cover-letters';
  try {
    const rows = await apiFetch(path);
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const sorted = rows.slice().sort((a, b) => {
      const ta = new Date(a?.created_at || 0).getTime();
      const tb = new Date(b?.created_at || 0).getTime();
      return tb - ta;
    });
    const cutoff = typeof opts?.submittedAtMs === 'number'
      ? opts.submittedAtMs - 30 * 1000
      : null;
    for (const row of sorted) {
      const ts = row?.created_at ? new Date(row.created_at).getTime() : 0;
      if (!ts) continue;
      if (cutoff != null && ts < cutoff) continue;
      return row;
    }
    return null;
  } catch (err) {
    // Read-only failures must not block the caller — they'll fall
    // through to the legacy generate-anyway path.
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[timeoutRecovery] lookup failed', err?.message || err);
    }
    return null;
  }
}

/**
 * Detect whether a thrown error matches the 30s ingress proxy
 * timeout family. We treat any of:
 *   • HTTP 504 / 503
 *   • status === 0 (network / abort)
 *   • TypeError "fetch failed"
 *   • Native AbortError
 * …as a recoverable timeout. The shape of `apiFetch`'s error
 * object varies (it sometimes rethrows the Response and sometimes
 * a thin Error-with-status), so we sniff defensively.
 */
function isTimeoutError(err) {
  if (!err) return false;
  const status = err.status ?? err.code ?? err.response?.status;
  if (status === 504 || status === 503 || status === 502) return true;
  if (status === 0) return true;
  const msg = String(err.message || err.detail || err || '').toLowerCase();
  if (msg.includes('upstream') && msg.includes('timeout')) return true;
  if (msg.includes('stream timeout')) return true;
  if (msg.includes('failed to fetch')) return true;
  // RC 1.49.1 P0-4 — XHR onerror rejects with TypeError("Network
  // request failed") (see lib/api.js xhrRequest). Treat as a
  // recoverable network/timeout error.
  if (msg.includes('network request failed')) return true;
  if (err.name === 'AbortError') return true;
  return false;
}

// Exposed for analyseFitPolling.js + Jest pinning.
export { isTimeoutError, isTimeoutError as isTimeoutErrorPublic };

/**
 * @param {Object} opts
 * @param {() => Promise<object>} opts.generate         The original
 *   apiFetch generation call. Returns the created artefact.
 * @param {'cv'|'letter'} opts.kind
 * @param {string} opts.jobId                            Required for
 *   the recovery lookup — without a jobId we can't disambiguate.
 * @param {(state: 'checking'|'recovered'|'failed') => void=} opts.onRecoveryState
 *   Optional UI banner hook. Call sites typically map these to a
 *   "We're checking your results…" → "Result completed
 *   successfully" / "Couldn't retrieve" banner.
 * @param {number=} opts.maxRetries                      Default 10.
 * @param {number=} opts.intervalMs                      Default 10000.
 * @returns {Promise<object>}                            The artefact
 *   row (either the original generation result, or the recovered
 *   row from a background-completed call).
 *
 * RC 1.49.1 P0-FIN (2026-05-08) — recovery window widened from
 *   `2 × 8s = 16s` to `10 × 10s = 100s` after the proxy 504. With
 *   the 30s ingress timeout this gives the backend ~130s of total
 *   wall-clock budget to surface a persisted artefact before the
 *   client falls through to the explicit "could not confirm"
 *   guidance copy. Covers the long tail of CV generation latency
 *   (large source CVs, gpt-5.2 throttle events) and slashes the
 *   double-charge incidence by widening the window the
 *   pre-existing idempotency key reuse can ride on.
 */
export async function withTimeoutRecovery({
  generate, kind, jobId, onRecoveryState,
  maxRetries = 10, intervalMs = 10000,
}) {
  // RC 1.49.1 P0-3 — capture submit time BEFORE generate() so the
  // recovery lookup can bind to a row freshly persisted by *this*
  // call rather than any older artefact in the user's list.
  const submittedAtMs = Date.now();
  try {
    return await generate();
  } catch (err) {
    if (!isTimeoutError(err) || !jobId) throw err;

    onRecoveryState?.('checking');

    for (let attempt = 0; attempt < maxRetries; attempt += 1) {
      await new Promise((r) => setTimeout(r, intervalMs));
      const found = await findExistingArtefact(kind, jobId, { submittedAtMs });
      if (found) {
        onRecoveryState?.('recovered');
        return found;
      }
    }
    onRecoveryState?.('failed');
    throw err;
  }
}

/**
 * RC 1.49.1 P0-2 — Analyse Fit recovery helper.
 *
 * Poll `GET /api/jobs/{jobId}/best-fit` looking for a `job_scores`
 * row that matches the request body's source_type/source_id and was
 * `evaluated_at` AFTER the submission timestamp. This handles the
 * case where `POST /analyze-fit` itself 504s at the 30s ingress
 * proxy boundary but the backend completed the run and persisted the
 * row in the background.
 *
 * @param {string} jobId
 * @param {{source_type?: string, source_id?: string}} requestBody
 * @param {number} submittedAtMs   Date.now() captured BEFORE the POST.
 * @returns {Promise<object|null>} The matching `BestFitCachedRow`, or null.
 */
export async function findRecentFitForSource(jobId, requestBody, submittedAtMs) {
  if (!jobId) return null;
  try {
    const res = await apiFetch(`/api/jobs/${jobId}/best-fit`);
    const rows = res?.all_cached || [];
    if (!rows.length) return null;
    const wantSourceType = requestBody?.source_type || null;
    const wantSourceId   = requestBody?.source_id || null;
    // Match strategy:
    //   1. If the request specified an explicit source, require that
    //      source on the row (prevents binding to a stale primary-CV
    //      row from a previous run).
    //   2. Otherwise (legacy "no source" call), accept the latest
    //      uploaded_cv row (mirrors backend default).
    //   3. Always require evaluated_at > submittedAtMs - 30s
    //      (the 30s slack absorbs any clock drift between the
    //      browser and the DB).
    const cutoff = submittedAtMs - 30 * 1000;
    for (const row of rows) {
      const evalAtMs = row?.evaluated_at
        ? new Date(row.evaluated_at).getTime()
        : 0;
      if (!evalAtMs || evalAtMs < cutoff) continue;
      if (wantSourceType && wantSourceId) {
        if (row.source_type === wantSourceType && row.source_id === wantSourceId) {
          return row;
        }
      } else if (row.source_type === 'uploaded_cv') {
        return row;
      }
    }
    return null;
  } catch (err) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[timeoutRecovery] best-fit lookup failed', err?.message || err);
    }
    return null;
  }
}

/**
 * RC 1.49.1 P0-3 — Pre-flight reuse helper for CV variants.
 *
 * Look for an existing CV variant tied to this job via the
 * `job_scores` ladder (the canonical job ↔ variant linkage in
 * NDORFlow's schema). Returns the variant_id of the latest
 * `cv_variant` row in `/best-fit.all_cached`, or null when there
 * is no analysed variant for this job yet.
 */
export async function findLatestVariantForJob(jobId) {
  if (!jobId) return null;
  try {
    const res = await apiFetch(`/api/jobs/${jobId}/best-fit`);
    const rows = res?.all_cached || [];
    // Rows are already ordered created_at.desc by the backend.
    for (const row of rows) {
      if (row?.source_type === 'cv_variant' && row?.source_id && !row?.stale) {
        return row.source_id;
      }
    }
    return null;
  } catch (err) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[timeoutRecovery] best-fit lookup for variant reuse failed', err?.message || err);
    }
    return null;
  }
}

/**
 * RC 1.49.1 P0-3 — Pre-flight reuse helper for cover letters.
 *
 * Look for an existing cover letter for this job. Lookup order:
 *   1. `application_packs?job_id=X` (client-filtered) — if a pack
 *      exists for this job AND carries a `cover_letter_id`, return
 *      that letter id directly.
 *   2. Otherwise return null. (We deliberately do NOT scan
 *      `/api/cover-letters` and bind by `cv_variant_id` because
 *      letters can be tied to variants used across multiple jobs.)
 */
export async function findLatestLetterForJob(jobId) {
  if (!jobId) return null;
  try {
    const res = await apiFetch(
      `/api/application-packs?job_id=${encodeURIComponent(jobId)}`,
    );
    const items = Array.isArray(res) ? res : (res?.items || []);
    const matched = items
      .filter((p) => p?.job_id === jobId && p?.cover_letter_id)
      .sort((a, b) => {
        const ta = new Date(a?.created_at || 0).getTime();
        const tb = new Date(b?.created_at || 0).getTime();
        return tb - ta;
      });
    return matched[0]?.cover_letter_id || null;
  } catch (err) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[timeoutRecovery] pack lookup for letter reuse failed', err?.message || err);
    }
    return null;
  }
}

/** Format helper — used by the "Generated 2 minutes ago" caption. */
export function formatRelativeTime(iso) {
  if (!iso) return '';
  const ts = new Date(iso).getTime();
  if (!ts) return '';
  const diffMs = Date.now() - ts;
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  return `${day} day${day === 1 ? '' : 's'} ago`;
}
